import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useEnvironment,
    usePromise,
    getApplications,
    deleteConsent,
    ClientRepresentation
} from '@keycloak/keycloak-account-ui';
import { Page, Button, TextSkeleton, CardSkeleton } from '../components';
import type { AccountEnvironment } from '@keycloak/keycloak-account-ui';
import { FileText, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { showErrorToast, showSuccessToast } from '../utils';
import { notifyAccountReady } from '../utils/startupOverlay';

export const Applications: React.FC = () => {
    const { t } = useTranslation();
    const context = useEnvironment<AccountEnvironment>();

    const [applications, setApplications] = useState<ClientRepresentation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(true);

    // Fetch applications data
    usePromise(
        async (signal) => {
            const data = await getApplications({ signal, context });
            setIsDataLoading(false);
            return data;
        },
        (data: ClientRepresentation[]) => {
            setApplications(data);
        }
    );

    useEffect(() => {
        if (!isDataLoading) {
            notifyAccountReady();
        }
    }, [isDataLoading]);

    const refreshApplications = async () => {
        try {
            setIsLoading(true);
            const data = await getApplications({ context });
            setApplications(data);
        } catch (error) {
            showErrorToast(t("applications.fetchError", "Failed to fetch applications"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleRevokeConsent = async (application: ClientRepresentation) => {
        if (!application.consent?.grantedScopes?.length) {
            return;
        }

        if (!window.confirm(t("applications.revokeConsent.confirm", "Are you sure you want to revoke access for this application?"))) {
            return;
        }

        try {
            setIsLoading(true);
            await deleteConsent(context, application.clientId);
            showSuccessToast(t("applications.revokeConsent.success", "Access revoked successfully"));
            await refreshApplications();
        } catch (error) {
            showErrorToast(t("applications.revokeConsent.error", "Failed to revoke access"));
        } finally {
            setIsLoading(false);
        }
    };

    const getCustomApplicationIconName = (application: ClientRepresentation) => {
        const clientId = application.clientId?.toLowerCase() ?? "";
        const clientName = application.clientName?.toLowerCase() ?? "";
        const effectiveUrl = application.effectiveUrl?.toLowerCase() ?? "";

        if (
            clientId.includes("account-console") ||
            clientName.includes("личный кабинет") ||
            clientName.includes("account console") ||
            effectiveUrl.includes("/account/")
        ) {
            return "account-console";
        }

        if (clientName.includes("gitlab") || clientId.includes("gitlab")) {
            return "gitlab";
        }

        if (clientName.includes("youtrack") || clientId.includes("youtrack")) {
            return "youtrack";
        }

        if (clientName.includes("grafana") || clientId.includes("grafana")) {
            return "grafana";
        }

        return undefined;
    };

    const getApplicationIcon = (application: ClientRepresentation) => {
        const customIconName = getCustomApplicationIconName(application);

        if (customIconName) {
            return (
                <img
                    src={`${context.environment.resourceUrl}/application-icons/${customIconName}.svg`}
                    alt={application.clientName || application.clientId}
                    className="h-8 w-8 object-contain"
                />
            );
        }

        if (application.logoUri) {
            return (
                <img
                    src={application.logoUri}
                    alt={application.clientName || application.clientId}
                    className="w-8 h-8 rounded"
                />
            );
        }

        // Default application icon
        return (
            <div className="w-8 h-8 bg-hover-light rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-accent-primary" />
            </div>
        );
    };

    const getApplicationDisplayName = (application: ClientRepresentation) => {
        const rawName = application.clientName?.trim();
        const templateKey =
            rawName?.startsWith("${") && rawName.endsWith("}")
                ? rawName.slice(2, -1)
                : undefined;

        if (templateKey) {
            return t(templateKey, application.clientId);
        }

        if (!rawName || /^\$\{.+\}$/.test(rawName)) {
            return application.clientId;
        }

        return rawName;
    };

    const openApplication = (application: ClientRepresentation) => {
        if (!application.effectiveUrl) {
            return;
        }

        window.location.assign(application.effectiveUrl);
    };

    const renderApplicationSummary = (application: ClientRepresentation) => (
        <>
            <div className="space-y-3 md:hidden">
                <div className="flex items-start gap-4">
                    <div className="shrink-0 rounded-xl border border-border-primary p-3">
                        {getApplicationIcon(application)}
                    </div>

                    <div className="min-w-0">
                        <div className="mb-2 flex flex-col gap-2">
                            <h3 className="break-words text-lg font-medium text-text-primary">
                                {getApplicationDisplayName(application)}
                            </h3>
                            <div className="self-start">
                                {getStatusBadge(application)}
                            </div>
                        </div>
                    </div>
                </div>

                {application.effectiveUrl && (
                    <p className="break-words text-sm text-text-secondary">
                        {t("applications.url", "URL")}: <span className="text-accent-primary break-all">{application.effectiveUrl}</span>
                    </p>
                )}
            </div>

            <div className="hidden min-w-0 md:flex md:items-start md:gap-4">
                <div className="shrink-0 rounded-xl border border-border-primary p-3">
                    {getApplicationIcon(application)}
                </div>

                <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <h3 className="break-words text-lg font-medium text-text-primary">
                            {getApplicationDisplayName(application)}
                        </h3>
                        <div className="self-start">
                            {getStatusBadge(application)}
                        </div>
                    </div>

                    {application.effectiveUrl && (
                        <p className="break-words text-sm text-text-secondary">
                            {t("applications.url", "URL")}: <span className="text-accent-primary break-all">{application.effectiveUrl}</span>
                        </p>
                    )}
                </div>
            </div>
        </>
    );

    const getStatusBadge = (application: ClientRepresentation) => {
        if (application.inUse) {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-xl text-xs font-medium bg-success-100 text-success-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t("applications.active", "Active")}
                </span>
            );
        }

        return (
            <span className="inline-flex items-center px-2 py-1 rounded-xl text-xs font-medium bg-secondary-100 text-secondary-800">
                <XCircle className="w-3 h-3 mr-1" />
                {t("applications.inactive", "Inactive")}
            </span>
        );
    };

    return (
        <Page
            title={t("applications.title", "Applications")}
            description={t("applications.description", "Manage applications that have access to your account")}
            className="mt-4 p-4"
        >
            <div className="space-y-6">
                {/* Applications Section */}
                <div className="rounded-2xl border border-border-primary p-6 shadow-sm">
                    {isDataLoading ? (
                        <>
                            {/* Header Skeleton */}
                            <div className="mb-6">
                                <TextSkeleton width="120px" height="28px" />
                                <div className="mt-2">
                                    <TextSkeleton width="380px" height="14px" />
                                </div>
                            </div>

                            {/* Applications List Skeleton */}
                            <div className="space-y-4">
                                <CardSkeleton showAvatar={true} lines={2} showActions={true} />
                                <CardSkeleton showAvatar={true} lines={1} showActions={true} />
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Real Header */}
                            <div className="border-l-4 border-accent-primary pl-4 mb-6">
                                <h2 className="text-xl font-semibold text-text-primary">
                                    {t("applications.title", "Applications")}
                                </h2>
                                <p className="text-sm text-text-secondary mt-1">
                                    {t("applications.description", "Manage applications that have access to your account")}
                                </p>
                            </div>

                            {/* Real Applications List */}
                            <div className="space-y-4">
                                {applications.length > 0 ? (
                                    applications.map((application) => (
                                        <div
                                            key={application.clientId}
                                            className="overflow-hidden rounded-2xl border border-border-primary p-4 transition-colors hover:bg-hover-light"
                                        >
                                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                                {application.effectiveUrl ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openApplication(application)}
                                                        className="min-w-0 flex-1 rounded-xl text-left transition-colors hover:bg-hover-light focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                                                    >
                                                        {renderApplicationSummary(application)}
                                                    </button>
                                                ) : (
                                                    <div className="min-w-0 flex-1">
                                                        {renderApplicationSummary(application)}
                                                    </div>
                                                )}

                                                {/* Actions */}
                                                <div className="flex-shrink-0 self-start md:self-center">
                                                    {application.consent?.grantedScopes && application.consent.grantedScopes.length > 0 && (
                                                        <Button
                                                            variant="outline"
                                                            onClick={() => handleRevokeConsent(application)}
                                                            loading={isLoading}
                                                            loadingText={t("applications.revoking", "Revoking...")}
                                                            leftIcon={<Trash2 className="w-4 h-4" />}
                                                            className="border-error-300 text-error-700 hover:bg-error-50 focus:ring-error-500"
                                                        >
                                                            {t("applications.revokeAccess", "Revoke access")}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="mx-auto h-12 w-12 text-text-tertiary mb-4">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-medium text-text-primary mb-2">
                                            {t("applications.empty.title", "No applications")}
                                        </h3>
                                        <p className="text-text-tertiary">
                                            {t("applications.empty.description", "You haven't granted access to any applications yet.")}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Page>
    );
};

export default Applications;
